import React, { useState, useEffect, useMemo } from 'react';
import Button from './Button';
import ExportFormatModal from './ExportFormatModal';
import { locationsToGeoJSONFeatureCollection } from '../utils/geoExport';
import './ExportPanel.css';

const ExportPanel = ({ locations, currentTime, datasetName, segments }) => {
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // Initialize start/end times when locations change
  useEffect(() => {
    if (locations && locations.length > 0) {
      setStartTime(locations[0].timestamp);
      setEndTime(locations[locations.length - 1].timestamp);
    }
  }, [locations]);

  // Calculate the number of locations in the selected range
  const selectedLocations = useMemo(() => {
    if (!locations || !startTime || !endTime) return [];
    return locations.filter(loc => loc.timestamp >= startTime && loc.timestamp <= endTime);
  }, [locations, startTime, endTime]);

  // Calculate duration in the selected range
  const duration = useMemo(() => {
    if (!startTime || !endTime) return 0;
    return endTime - startTime;
  }, [startTime, endTime]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--:--';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString();
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds <= 0) return '0s';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    return parts.join(' ');
  };

  const handleSetStart = () => {
    if (currentTime != null) {
      setStartTime(currentTime);
    }
  };

  const handleSetEnd = () => {
    if (currentTime != null) {
      setEndTime(currentTime);
    }
  };

  // Format timestamp for GPX (ISO 8601 format)
  const formatGPXTime = (timestamp) => {
    const date = new Date(timestamp * 1000);
    return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
  };

  // Convert locations to GPX format
  const generateGPX = () => {
    if (selectedLocations.length === 0) return null;

    let gpxContent = `<?xml version="1.0"?>
<gpx version="1.1" creator="Offboard Data Explorer">
`;

    selectedLocations.forEach((loc) => {
      const { lat, lon, timestamp, altitude } = loc;
      
      let timeStr = '';
      if (timestamp) {
        timeStr = `
        <time>${formatGPXTime(timestamp)}</time>`;
      }
      
      let eleStr = '';
      if (altitude != null) {
        eleStr = `
        <ele>${altitude}</ele>`;
      }
      
      gpxContent += `    <wpt lat="${lat}" lon="${lon}">${timeStr}${eleStr}
    </wpt>
`;
    });

    gpxContent += `</gpx>`;
    return gpxContent;
  };

  const handleExport = () => {
    if (selectedLocations.length === 0) return;

    setExporting(true);

    try {
      const gpxContent = generateGPX();
      if (!gpxContent) {
        setExporting(false);
        return;
      }

      // Create blob and trigger download
      const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      // Create filename from dataset name and time range
      const startStr = new Date(startTime * 1000).toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `${(datasetName || 'export').replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${startStr}.gpx`;
      a.download = filename;
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const isValidRange = startTime != null && endTime != null && startTime < endTime && selectedLocations.length > 0;

  const handleDownloadGeoJson = () => {
    if (!locations || locations.length === 0) return;
    setExporting(true);
    try {
      const geojson = locationsToGeoJSONFeatureCollection(locations, segments || []);
      const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/geo+json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const filename = `${(datasetName || 'dataset').replace(/[^a-z0-9]/gi, '_').toLowerCase()}.geojson`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  };

  const handleExportFormatChoice = (format) => {
    setExportModalOpen(false);
    if (format === 'gpx') {
      handleExport();
    } else {
      handleDownloadGeoJson();
    }
  };

  return (
    <div className="export-panel">
      <h2 className="export-panel-title">Export</h2>
      
      <div className="export-instructions">
        <p>Use the timeline to set Start/End for GPX export range. Then click Export to choose format.</p>
      </div>

      <div className="export-time-section">
        <div className="export-time-row">
          <div className="export-time-info">
            <span className="export-time-label">Start Time</span>
            <span className="export-time-value">{formatTime(startTime)}</span>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={handleSetStart}
            disabled={currentTime == null}
          >
            Set Start
          </Button>
        </div>

        <div className="export-time-row">
          <div className="export-time-info">
            <span className="export-time-label">End Time</span>
            <span className="export-time-value">{formatTime(endTime)}</span>
          </div>
          <Button
            variant="outline"
            size="small"
            onClick={handleSetEnd}
            disabled={currentTime == null}
          >
            Set End
          </Button>
        </div>
      </div>

      <div className="export-summary">
        <div className="export-summary-item">
          <span className="export-summary-label">Duration</span>
          <span className="export-summary-value">{formatDuration(duration)}</span>
        </div>
        <div className="export-summary-item">
          <span className="export-summary-label">Points</span>
          <span className="export-summary-value">{selectedLocations.length}</span>
        </div>
      </div>

      {!isValidRange && startTime != null && endTime != null && startTime >= endTime && (
        <div className="export-error">
          Start time must be before end time
        </div>
      )}

      <div className="export-actions">
        <Button
          variant="primary"
          size="medium"
          onClick={() => setExportModalOpen(true)}
          disabled={exporting || (locations && locations.length === 0)}
          className="export-button"
        >
          {exporting ? 'Exporting...' : 'Export'}
        </Button>
      </div>

      <ExportFormatModal
        isOpen={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onSelectFormat={handleExportFormatChoice}
        disabled={exporting}
        gpxDisabled={!isValidRange}
      />
    </div>
  );
};

export default ExportPanel;




